import Button from '@mui/material/Button';
import React, { ReactElement, useEffect, useState } from 'react';
import EventSequenceService from '../../../../services/event-sequence.service';
import { toast } from 'react-toastify';
import {  PsfTemplate, PsfTemplateItem, PsfPresenceLink } from '../../../../@types/occurrence-dna';
import Paper , { PaperProps } from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';

import Draggable from 'react-draggable';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { TreeView } from '@mui/x-tree-view/TreeView';

import { CanvasEnvironment } from '../../../../utils/canvas/canvas-environment';
import CheckBox  from '@mui/material/Checkbox';
import {  FormControlLabel } from '@mui/material';
import * as canvasActions from '../../../../utils/canvas/canvas-actions';

function PaperComponent(props: PaperProps): ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}


export default function PsfCodeSelectorModal(props: { open: boolean, env: CanvasEnvironment | null, onClose: () => void }): ReactElement {
    const { open, env, onClose } = props;
    
    
    const [esdPsfTemplate, setEsdPsfTemplate] = useState<PsfTemplate | null>(null);
    const [checked, setChecked] = React.useState<PsfPresenceLink[]>([]);
    const [isRunning, setIsRunning] = React.useState<boolean>(false);

    const fetchEsdPsfTemplate = (): void => {
        if (!env?.eventSequence) return;
        setIsRunning(true);
        EventSequenceService.getPsfTemplate(env.eventSequence.id).then((response) => {
            setEsdPsfTemplate(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setIsRunning(false);
        });
    }

    const fetchEsdPsfPresenceLinks = (): void => {
        if (!env?.eventSequence) return;
        setIsRunning(true);

        EventSequenceService.getPsfPresenceLinks(env.eventSequence.id, [], []).then((response) => {
            let selection = env.selection[0];
            setChecked(response.filter((link) => link.faultTreeNodeId === selection.get('data').id));
        }).catch((error: Error) => {
            setChecked([]);
            toast.error(error.message);
        }).finally(() => {
            setIsRunning(false);
        });
    }


    useEffect(() => {
        if (open) {
            fetchEsdPsfTemplate();
            fetchEsdPsfPresenceLinks();
        }
        
    }, [open]);


    const handleSave = async (): Promise<any> => {
        let selection = env!.selection[0];
        // need to first delete all the psf presence links for the fault tree node
        // otherwise, the save will fail if there are existing links
        await EventSequenceService.savePsfPresencesByFaultTreeNodeId(selection.get('data').id, []);
        return EventSequenceService.savePsfPresencesByFaultTreeNodeId(selection.get('data').id, checked);
    }

    const fetchPsfCounts = async (): Promise<any> => {
        let eventSequence = env?.eventSequence;
        if (!eventSequence) return;
        let occurrenceIds: Array<number> = [];
        let psfIds: Array<number> = [];
        return EventSequenceService.getPsfPresenceStat(eventSequence.id, occurrenceIds, psfIds);
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            fullWidth
            maxWidth="sm">
            <DialogTitle style={{ cursor: 'move'}} id='draggable-dialog-title'>
                Performance Shaping Factor (PSF) Code Selector
            </DialogTitle>
            <DialogContent dividers sx={{ height: 300, overflow: 'auto'}}>
                <TreeView
                    aria-label="controlled"
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                >
                    {esdPsfTemplate && esdPsfTemplate.psfTemplateItems && Object.entries(Object.groupBy(esdPsfTemplate.psfTemplateItems.sort((a, b) => a.category.localeCompare(b.category)), ({ category }) => category)).map(([category, items]) => (
                        <TreeItem key={category} nodeId={category} label={category}>
                            {items!.map((item: PsfTemplateItem) => (
                                <TreeItem
                                    key={item.id}
                                    nodeId={item.id!.toString()}
                                    label={                                  
                                        <FormControlLabel
                                            control={
                                                <CheckBox
                                                    checked={checked.some((i) => i.psfTemplateItemId === item.id)}
                                                    onChange={
                                                        (event): void => {
                                                            if (event.target.checked) {
                                                                setChecked([...checked, { psfTemplateItemId: item.id, faultTreeNodeId: env?.selection[0].get('data').id, occurrenceId: env!.currentLinkedOccurrenceDna!.occurrenceId }]);
                                                            } else {
                                                                setChecked(checked.filter((i) => i.psfTemplateItemId !== item.id));
                                                            }
                                                        }
                                                    }
                                                    disableRipple
                                                />
                                            }
                                            label={item.name}
                                        />
                                    }
                                />
                            ))}
                        </TreeItem>
                    ))}
                </TreeView>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<CancelIcon />}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={isRunning ? <CircularProgress size={20} /> : <SaveAltIcon />}
                    onClick={ async (): Promise<void> => {
                        setIsRunning(true);
                        try {
                            await handleSave();
                            toast.success('Performance shaping factor saved successfully');
                            const psfCounts = await fetchPsfCounts();
                            if (env && esdPsfTemplate && psfCounts) {
                                canvasActions.addPsfsToNode(env,esdPsfTemplate, psfCounts, env.selection[0]);
                            }
                        } catch (error: any) {
                            toast.error(error.message);
                        } finally { 
                            setIsRunning(false);
                            onClose();
                        }                   
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>

    );
}
